//Hook to unify auth stuff, useAuth hook
import { RootState } from '@app/redux/types';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import LocalStorage from '@app/utils/LocalStorage';
import { useNicheAuth } from './useNicheAuth';
import { usePropelAuthBridge } from './PropelAuthBridge';

export const useAuth = () => {
	// Auth0 provider hooks
	const {
		isLoading: auth0IsLoading,
		user: auth0User,
		isAuthenticated: auth0IsAuthenticated,
		getAccessTokenSilently: getAccessTokenSilentlyAuth0,
		logout: auth0Logout,
		loginWithRedirect: auth0LoginWithRedirect,
	} = useAuth0();

	// Propel provider state, read through a bridge context so this hook works
	// whether or not the PropelAuth provider is mounted. See PropelAuthBridge.
	const {
		loading: propelIsLoading,
		user: propelUser,
		isLoggedIn: propelIsLoggedIn,
		accessToken: propelAccessToken,
		redirectToLoginPage,
		logout: propelLogout,
	} = usePropelAuthBridge();

	// Niche authentication hook
	const {
		isLoading: nicheIsLoading,
		user: nicheUser,
		isAuthenticated: nicheIsAuthenticated,
		logout: nicheLogout,
		getAccessTokenSilently: nicheGetAccessTokenSilently,
		loginWithRedirect: nicheLoginWithRedirect,
	} = useNicheAuth();

	// Grant access / identity information
	const {
		user: accessUser,
		loading: grantAccessLoading,
		error: accessError,
	} = useSelector(({ auth }: RootState) => ({
		user: auth.user,
		loading: !!auth.loading,
		error: auth.error,
	}));

	const selectedAuthProviderName = LocalStorage.getFromLocalStorage(
		'checkAuthProvider',
	) as 'auth0' | 'propelAuth' | 'niche';

	// Only the actively selected provider's loading state should gate the app.
	// Previously this OR'd all three providers together, so an unused provider
	// stuck loading forever (e.g. PropelAuth endlessly retrying a CORS-blocked
	// token refresh) would hang the app indefinitely even when the user had
	// authenticated with a different provider.
	const providerLoadingByName = {
		auth0: auth0IsLoading,
		propelAuth: propelIsLoading,
		niche: nicheIsLoading,
	};
	const isProviderLoading = selectedAuthProviderName
		? providerLoadingByName[selectedAuthProviderName]
		: false;
	const isAccessLoading = grantAccessLoading;

	const isLoading = isProviderLoading || isAccessLoading;

	const providersSchema = useMemo(() => {
		return {
			auth0: {
				name: 'auth0',
				user: auth0User,
				isAuthenticated: auth0IsAuthenticated,
				isLoading: auth0IsLoading,
				getAccessTokenSilently: getAccessTokenSilentlyAuth0,
				logout: auth0Logout,
				loginWithRedirect: auth0LoginWithRedirect,
			},
			propelAuth: {
				name: 'propelAuth',
				user: propelUser,
				isAuthenticated: propelIsLoggedIn || false,
				isLoading: propelIsLoading,
				getAccessTokenSilently: () => Promise.resolve(propelAccessToken),
				logout: propelLogout,
				loginWithRedirect: redirectToLoginPage,
			},
			niche: {
				name: 'niche',
				user: nicheUser,
				isAuthenticated: nicheIsAuthenticated,
				isLoading: nicheIsLoading,
				getAccessTokenSilently: nicheGetAccessTokenSilently,
				logout: nicheLogout,
				loginWithRedirect: nicheLoginWithRedirect,
			},
		};
	}, [
		auth0IsLoading,
		auth0IsAuthenticated,
		getAccessTokenSilentlyAuth0,
		auth0Logout,
		auth0LoginWithRedirect,
		auth0User,
		propelIsLoading,
		propelIsLoggedIn,
		propelAccessToken,
		propelLogout,
		redirectToLoginPage,
		propelUser,
		nicheIsLoading,
		nicheIsAuthenticated,
		nicheGetAccessTokenSilently,
		nicheLogout,
		nicheLoginWithRedirect,
		nicheUser,
	]);

	const authProvider = useMemo(() => {
		if (!selectedAuthProviderName) {
			return null;
		}

		return providersSchema[selectedAuthProviderName];
	}, [providersSchema, selectedAuthProviderName]);

	return {
		authProvider,
		authProvidersSchemas: providersSchema,
		selectedAuthProviderName,
		isLoading,
		isProviderLoading,
		isAccessLoading,
		accessError,
		user: accessUser,
		propelAccessToken,
		// Expose niche user information
		isNicheUser: selectedAuthProviderName === 'niche',
		nicheUser,
	};
};
